'use client';

import { useMemo } from 'react';
import { SearchInputGroup } from '~/components/input-group/search-input-group';
import { MapAreaItem } from '~/lib/map-area-prefectures';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '~/components/ui/accordion';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { AreaForm } from '~/components/form/area-form';
import { LicenseForm } from '~/components/form/license-form';
import { CategoryForm } from '~/components/form/category-form';
import { TypeForm } from '~/components/form/type-form';
import { AnnualSalaryForm } from '~/components/form/annual-salary-form';
import { FeatureForm } from '~/components/form/feature-form';
import { ContractForm } from '~/components/form/contract-form';
import { useRouter } from 'next/navigation';
import { generatePath } from '~/lib/generate-path';
import type { MasterData, ParentItem } from '~/types/master-data';
import type { SearchConditions } from '~/types/search-conditions';
import type { ConditionParam } from '~/types/condition-param';

interface JobSearchPcAccordionProps {
  masterData: MasterData;
  mappedAreas: MapAreaItem[];
  mappedLicenses: ParentItem[];
  conditionParams: ConditionParam[];
  updateParentChecked: (
    item: ParentItem,
    type: keyof SearchConditions,
    checked: boolean,
  ) => void;
  updateChecked: (
    type: keyof SearchConditions,
    name: string,
    checked: boolean,
  ) => void;
  resetConditions: () => void;
  searchConditions: SearchConditions;
  setSearchConditions: (searchConditions: SearchConditions) => void;
}

export function JobSearchPcAccordion(props: JobSearchPcAccordionProps) {
  // チェック状態を管理するため各項目でSetを使用
  const categoryNamesSet = useMemo(
    () => new Set(props.searchConditions.categoryNames),
    [props.searchConditions.categoryNames],
  );

  const router = useRouter();
  const searchHandler = () => {
    const path = generatePath(props.searchConditions, props.masterData);
    router.push(path);
  };

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="search-conditions">
        <AccordionPrimitive.Header className="flex" asChild>
          <h2>
            <AccordionPrimitive.Trigger className="c-button-heading-toggle no-underline hover:no-underline u-mt20 flex flex-1 items-center justify-between py-4 font-medium transition-all">
              検索条件を変更する
            </AccordionPrimitive.Trigger>
          </h2>
        </AccordionPrimitive.Header>
        <AccordionContent>
          <div className="p-side-search-form c-panel !mt-0">
            <div className="p-side-search-form__section !py-4">
              <SearchInputGroup
                freeWord={props.searchConditions.freeWord}
                setFreeWord={(freeWord) =>
                  props.setSearchConditions({
                    ...props.searchConditions,
                    freeWord,
                  })
                }
                searchHandler={searchHandler}
              />
            </div>
            <CategoryForm
              masterData={props.masterData}
              categoryNamesSet={categoryNamesSet}
              updateChecked={props.updateChecked}
            />
            <TypeForm
              masterData={props.masterData}
              searchConditions={props.searchConditions}
              updateChecked={props.updateChecked}
            />
            <LicenseForm
              mappedLicenses={props.mappedLicenses}
              searchConditions={props.searchConditions}
              updateParentChecked={props.updateParentChecked}
              updateChecked={props.updateChecked}
            />
            <AreaForm
              mappedAreas={props.mappedAreas}
              searchConditions={props.searchConditions}
              updateParentChecked={props.updateParentChecked}
              updateChecked={props.updateChecked}
            />
            <div className="p-side-search-form__section u-pb16">
              <div className="!py-1 mt-3 !mb-0 p-side-search-form__section__title -other">
                こだわり条件
              </div>
              <AnnualSalaryForm
                searchConditions={props.searchConditions}
                setSearchConditions={props.setSearchConditions}
              />
              <ContractForm
                masterData={props.masterData}
                searchConditions={props.searchConditions}
                updateChecked={props.updateChecked}
              />
              <FeatureForm
                masterData={props.masterData}
                searchConditions={props.searchConditions}
                updateChecked={props.updateChecked}
              />
            </div>
            <div className="u-flex -justify-center u-gap8 u-py15 u-pos-rel">
              <button
                type="button"
                className="p-conditions__clear-link"
                onClick={props.resetConditions}
              >
                検索条件をクリア
              </button>
              <button
                type="button"
                className="c-button -green -large leading-none"
                onClick={searchHandler}
              >
                この条件で検索する
              </button>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
